'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LuChevronLeft, LuChevronRight, LuDownload } from 'react-icons/lu';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

/** Pomer strán jednej strany katalógu (šírka / výška). */
const PAGE_RATIO = 1205 / 1693;
/** Musí sedieť s triedou `duration-700` na liste knihy. */
const FLIP_DURATION_MS = 700;
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

export interface CatalogFlipbookLabels {
  previous: string;
  next: string;
  download: string;
  page: string;
}

export interface CatalogFlipbookProps {
  /** Obrázky strán katalógu v poradí, v akom sa listujú. */
  pages: string[];
  pdfUrl: string;
  title: string;
  labels: CatalogFlipbookLabels;
}

interface Leaf {
  front: string | null;
  back: string | null;
  frontNumber: number;
  backNumber: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Prvá strana je obálka, ďalej sa listuje po dvojstranách – rovnako ako v tlačenej brožúre. */
const spreadToPage = (spread: number) => (spread === 0 ? 0 : spread * 2 - 1);
const pageToSpread = (page: number) => (page === 0 ? 0 : Math.floor((page + 1) / 2));

export function CatalogFlipbook({ pages, pdfUrl, title, labels }: CatalogFlipbookProps) {
  const leaves = useMemo<Leaf[]>(
    () =>
      Array.from({ length: Math.ceil(pages.length / 2) }, (_, index) => ({
        front: pages[index * 2] ?? null,
        back: pages[index * 2 + 1] ?? null,
        frontNumber: index * 2 + 1,
        backNumber: index * 2 + 2,
      })),
    [pages],
  );

  const lastSpread = leaves.length;
  const lastPage = pages.length - 1;

  const [spread, setSpread] = useState(0);
  const [page, setPage] = useState(0);
  const [flippingLeaf, setFlippingLeaf] = useState<number | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [loadedLeaves, setLoadedLeaves] = useState<number[]>([0, 1]);

  const trackRef = useRef<HTMLDivElement>(null);
  const flipTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const goToSpread = useCallback(
    (next: number) => {
      const target = clamp(next, 0, lastSpread);
      if (target === spread) return;

      setFlippingLeaf(Math.min(spread, target));
      clearTimeout(flipTimeoutRef.current);
      flipTimeoutRef.current = setTimeout(() => setFlippingLeaf(null), FLIP_DURATION_MS);

      setSpread(target);
      setPage(spreadToPage(target));
    },
    [lastSpread, spread],
  );

  const goToPage = useCallback(
    (next: number) => {
      const target = clamp(next, 0, lastPage);

      setPage(target);
      setSpread(pageToSpread(target));
    },
    [lastPage],
  );

  const scrollToSlide = useCallback((index: number) => {
    const slide = trackRef.current?.children[index] as HTMLElement | undefined;
    if (slide) trackRef.current?.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }, []);

  const goPrevious = useCallback(() => {
    if (isDesktop) {
      goToSpread(spread - 1);
      return;
    }

    const target = clamp(page - 1, 0, lastPage);
    goToPage(target);
    scrollToSlide(target);
  }, [goToPage, goToSpread, isDesktop, lastPage, page, scrollToSlide, spread]);

  const goNext = useCallback(() => {
    if (isDesktop) {
      goToSpread(spread + 1);
      return;
    }

    const target = clamp(page + 1, 0, lastPage);
    goToPage(target);
    scrollToSlide(target);
  }, [goToPage, goToSpread, isDesktop, lastPage, page, scrollToSlide, spread]);

  const canGoPrevious = isDesktop ? spread > 0 : page > 0;
  const canGoNext = isDesktop ? spread < lastSpread : page < lastPage;

  useEffect(() => () => clearTimeout(flipTimeoutRef.current), []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const update = () => setIsDesktop(mediaQuery.matches);

    update();
    mediaQuery.addEventListener('change', update);

    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  /** Strany načítavame len v okolí aktuálnej dvojstrany, raz načítané necháme v DOM. */
  useEffect(() => {
    setLoadedLeaves(current => {
      const next = new Set(current);
      [spread - 1, spread, spread + 1].forEach(index => {
        if (index >= 0 && index < leaves.length) next.add(index);
      });

      return next.size === current.length ? current : Array.from(next);
    });
  }, [leaves.length, spread]);

  useEffect(() => {
    const track = trackRef.current;
    if (isDesktop || !track) return;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        const index = Number((visible.target as HTMLElement).dataset.index);
        if (Number.isFinite(index)) goToPage(index);
      },
      { root: track, threshold: 0.6 },
    );

    Array.from(track.children).forEach(child => observer.observe(child));

    return () => observer.disconnect();
  }, [goToPage, isDesktop, pages.length]);

  const counter = isDesktop
    ? spread === 0
      ? `1 / ${pages.length}`
      : `${spread * 2}${spread * 2 + 1 <= pages.length ? `–${spread * 2 + 1}` : ''} / ${pages.length}`
    : `${page + 1} / ${pages.length}`;

  const steps = isDesktop ? lastSpread : lastPage;
  const progress = steps > 0 ? ((isDesktop ? spread : page) / steps) * 100 : 0;

  return (
    <div className="flex w-full flex-col items-center gap-5 sm:gap-6">
      {isDesktop ? (
        <div className="w-full max-w-[880px]" style={{ perspective: '2400px' }}>
          {/* Zatvorená kniha je vycentrovaná, otvorená sa rozloží na celú dvojstranu. */}
          <div
            className="relative w-full transition-transform duration-700 ease-in-out [transform-style:preserve-3d]"
            style={{
              aspectRatio: `${PAGE_RATIO * 2}`,
              transform: `translateX(${spread === 0 ? '-25%' : spread === lastSpread ? '25%' : '0%'})`,
            }}
          >
            <div
              className={cn(
                'absolute inset-y-0 left-0 w-1/2 bg-[#F1F0EE] shadow-[0_18px_50px_-20px_rgba(0,0,0,0.45)] transition-opacity duration-300',
                spread > 0 ? 'opacity-100' : 'opacity-0',
              )}
            />
            <div
              className={cn(
                'absolute inset-y-0 right-0 w-1/2 bg-[#F1F0EE] shadow-[0_18px_50px_-20px_rgba(0,0,0,0.45)] transition-opacity duration-300',
                spread < lastSpread ? 'opacity-100' : 'opacity-0',
              )}
            />

            {leaves.map((leaf, index) => {
              const isFlipped = index < spread;

              return (
                <div
                  key={leaf.frontNumber}
                  className="absolute inset-y-0 left-1/2 w-1/2 transition-transform duration-700 ease-in-out [transform-style:preserve-3d]"
                  style={{
                    transformOrigin: 'left center',
                    transform: `rotateY(${isFlipped ? -180 : 0}deg)`,
                    zIndex:
                      index === flippingLeaf
                        ? leaves.length + 1
                        : isFlipped
                          ? index
                          : leaves.length - index,
                  }}
                >
                  <CatalogPageFace
                    side="front"
                    src={leaf.front}
                    pageNumber={leaf.frontNumber}
                    title={title}
                    isLoaded={loadedLeaves.includes(index)}
                  />
                  <CatalogPageFace
                    side="back"
                    src={leaf.back}
                    pageNumber={leaf.backNumber}
                    title={title}
                    isLoaded={loadedLeaves.includes(index)}
                  />
                </div>
              );
            })}

            {canGoPrevious && (
              <button
                type="button"
                onClick={goPrevious}
                aria-label={labels.previous}
                className="absolute inset-y-0 left-0 z-50 w-1/2 cursor-w-resize"
              />
            )}
            {canGoNext && (
              <button
                type="button"
                onClick={goNext}
                aria-label={labels.next}
                className="absolute inset-y-0 right-0 z-50 w-1/2 cursor-e-resize"
              />
            )}
          </div>
        </div>
      ) : (
        <div
          ref={trackRef}
          className="hide-scroll-bar flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain"
        >
          {pages.map((src, index) => (
            <div key={src} data-index={index} className="w-full flex-none snap-center px-1">
              <div
                className="relative mx-auto w-full bg-white shadow-[0_12px_32px_-16px_rgba(0,0,0,0.45)]"
                style={{ aspectRatio: `${PAGE_RATIO}` }}
              >
                <Image
                  src={src}
                  alt={`${title} – strana ${index + 1}`}
                  width={0}
                  height={0}
                  sizes="(max-width: 767px) 100vw, 50vw"
                  className="h-full w-full object-contain"
                  loading={index < 2 ? 'eager' : 'lazy'}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex w-full max-w-[880px] flex-col items-center gap-3 px-1 sm:gap-4">
        <div className="h-[3px] w-full bg-black/10">
          <div
            className="h-full bg-primary transition-[width] duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex w-full items-center justify-between gap-4">
          <Button
            variant="secondary"
            size="icon"
            onClick={goPrevious}
            disabled={!canGoPrevious}
            aria-label={labels.previous}
          >
            <LuChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>

          <span
            aria-live="polite"
            className="text-sm font-semibold tracking-tight text-muted-foreground sm:text-base"
          >
            {labels.page} {counter}
          </span>

          <Button
            variant="secondary"
            size="icon"
            onClick={goNext}
            disabled={!canGoNext}
            aria-label={labels.next}
          >
            <LuChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        </div>
      </div>

      <Button variant="default" size="default" asChild className="w-full sm:w-auto">
        <a href={pdfUrl} download target="_blank" rel="noopener noreferrer">
          <LuDownload className="h-5 w-5" />
          {labels.download}
        </a>
      </Button>
    </div>
  );
}

function CatalogPageFace({
  side,
  src,
  pageNumber,
  title,
  isLoaded,
}: {
  side: 'front' | 'back';
  src: string | null;
  pageNumber: number;
  title: string;
  isLoaded: boolean;
}) {
  return (
    <div
      className="absolute inset-0 overflow-hidden bg-white [backface-visibility:hidden]"
      style={{ transform: side === 'back' ? 'rotateY(180deg)' : undefined }}
    >
      {src && isLoaded && (
        <Image
          src={src}
          alt={`${title} – strana ${pageNumber}`}
          width={0}
          height={0}
          sizes="(max-width: 767px) 100vw, 50vw"
          className="h-full w-full object-contain"
          loading={pageNumber <= 3 ? 'eager' : 'lazy'}
        />
      )}

      {/* Tieň pri chrbte knihy – predná strana ho má vľavo, zadná vpravo. */}
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 w-[8%]',
          side === 'front'
            ? 'left-0 bg-gradient-to-r from-black/20 to-transparent'
            : 'right-0 bg-gradient-to-l from-black/20 to-transparent',
        )}
      />
    </div>
  );
}
